/**
 * くさび (wedge) / 隅くさび (cornerWedge) の単位形状データ。
 *
 * Box/Ball/Cylinder は three / rapier の組み込みプリミティブで表せるが、
 * wedge / cornerWedge は組み込みが無いため頂点から作る。描画（three の
 * BufferGeometry）と物理（rapier の convexHull）で同じ頂点定義を共有して
 * 見た目と当たり判定をずらさないために、ライブラリ非依存の純データとして置く。
 *
 * 単位形状は [-0.5, 0.5]^3 の立方体内に定義し、実寸は Part.Size で
 * スケールする（描画は container.scale、物理はハル点に size を乗算）。
 * Roblox 座標系（Y-up, -Z 前）に合わせる。
 */

type Vec3Tuple = readonly [number, number, number]

/**
 * くさび: YZ 断面が直角三角形。+Z 面が全高で -Z へ向かって下る斜面。
 * 幅は X 方向。Roblox WedgePart / Part.Shape=Wedge に対応。
 * 向きの根拠: RJF02 テンプレートの「すべり台」（wedge, 無回転）は +Z 端
 * （z=122.2）で「くろいゆか」（上面 y=32.9）と接続しており、+Z 側が全高で
 * ないと実機のコース（黒い床 → すべり台を滑り降りる）が成立しない。
 * 以前は -Z 側を全高にしていたため、全ステージの坂が前後逆（鏡像）だった。
 */
const WEDGE_VERTICES: readonly Vec3Tuple[] = [
  [-0.5, -0.5, 0.5], // 0 back-bottom-left（+Z = 全高側）
  [0.5, -0.5, 0.5], // 1 back-bottom-right
  [0.5, 0.5, 0.5], // 2 back-top-right
  [-0.5, 0.5, 0.5], // 3 back-top-left
  [-0.5, -0.5, -0.5], // 4 front-bottom-left（-Z = 斜面の裾）
  [0.5, -0.5, -0.5], // 5 front-bottom-right
]

/** くさびの三角形（頂点インデックス）。両面表示前提なので巻き順は厳密でない。 */
const WEDGE_FACES: readonly (readonly [number, number, number])[] = [
  [0, 1, 2],
  [0, 2, 3], // back (-Z)
  [0, 4, 5],
  [0, 5, 1], // bottom (-Y)
  [3, 2, 5],
  [3, 5, 4], // slope (top)
  [0, 3, 4], // left (-X)
  [1, 5, 2], // right (+X)
]

/**
 * 隅くさび: 矩形底面 + 1 つの上隅 (-X,-Z,+Y) を頂点とする四角錐状。
 * Roblox CornerWedgePart / Part.Shape=CornerWedge の近似（凸形状）。
 */
const CORNER_WEDGE_VERTICES: readonly Vec3Tuple[] = [
  [-0.5, -0.5, -0.5], // 0
  [0.5, -0.5, -0.5], // 1
  [0.5, -0.5, 0.5], // 2
  [-0.5, -0.5, 0.5], // 3
  [-0.5, 0.5, -0.5], // 4 apex
]

const CORNER_WEDGE_FACES: readonly (readonly [number, number, number])[] = [
  [0, 1, 2],
  [0, 2, 3], // base (-Y)
  [0, 1, 4],
  [1, 2, 4],
  [2, 3, 4],
  [3, 0, 4], // sides to apex
]

/** 面リストから非インデックスの頂点座標配列（フラット）を作る（フラットシェーディング用）。 */
const toTrianglePositions = (
  vertices: readonly Vec3Tuple[],
  faces: readonly (readonly [number, number, number])[],
): number[] => {
  const out: number[] = []
  for (const [a, b, c] of faces) {
    for (const index of [a, b, c]) {
      out.push(vertices[index][0], vertices[index][1], vertices[index][2])
    }
  }
  return out
}

/** ユニーク頂点をフラット配列にする（rapier convexHull 用）。 */
const toHullPoints = (vertices: readonly Vec3Tuple[]): Float32Array =>
  new Float32Array(vertices.flatMap((v) => [v[0], v[1], v[2]]))

/** three BufferGeometry 用の三角形頂点（非インデックス, フラット）。 */
export const WEDGE_TRIANGLE_POSITIONS: readonly number[] = toTrianglePositions(
  WEDGE_VERTICES,
  WEDGE_FACES,
)
export const CORNER_WEDGE_TRIANGLE_POSITIONS: readonly number[] =
  toTrianglePositions(CORNER_WEDGE_VERTICES, CORNER_WEDGE_FACES)

/** rapier convexHull 用の単位ハル点（フラット）。 */
export const WEDGE_HULL_POINTS: Float32Array = toHullPoints(WEDGE_VERTICES)
export const CORNER_WEDGE_HULL_POINTS: Float32Array = toHullPoints(
  CORNER_WEDGE_VERTICES,
)
